<template>
  <div class="add-tool-page">
    <!-- 顶部导航栏 -->
    <div class="top-bar">
      <button class="ghost-btn" @click="goBack">
        <span class="icon">←</span> 返回
      </button>
      <div class="title">{{ isEditMode ? '✏️ 编辑工具' : '动态脚本参数表单生成器' }}</div>
      <div class="actions">
        <button class="save-tool-btn" @click="saveTool" :disabled="saving">
          💾 {{ saving ? '保存中...' : (isEditMode ? '更新工具' : '保存到工具库') }}
        </button>
        <button v-if="!isEditMode" class="primary-btn" @click="resetToDefault">🔄 重置为默认示例</button>
      </div>
    </div>

    <!-- 全局设置区 -->
    <div class="global-settings">
      <div class="settings-card">
        <div class="settings-title">🔧 全局设置</div>
        <div class="settings-row">
          <div class="settings-item">
            <label class="settings-label">功能名称</label>
            <input v-model="globalConfig.name" type="text" class="settings-input" placeholder="例如：基因家族鉴定" />
          </div>
          <div class="settings-item flex-2">
            <label class="settings-label">脚本全路径</label>
            <input v-model="globalConfig.scriptPath" type="text" class="settings-input" placeholder="例如：perl /path/to/gene_family.pl" />
          </div>
        </div>
        <div class="settings-row" style="margin-top: 12px;">
          <div class="settings-item">
            <label class="settings-label">所属分类</label>
            <div class="category-select-row">
              <select v-model="globalConfig.category" class="settings-input" @change="onCategoryChange">
                <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                <option value="__new__">+ 新增分类</option>
              </select>
              <button v-if="globalConfig.category && globalConfig.category !== '__new__'" 
                      class="remove-category-btn" 
                      @click="removeCategory(globalConfig.category)" 
                      title="删除当前分类">🗑</button>
            </div>
            <div v-if="showCategoryInput" class="new-category-row">
              <input v-model="newCategoryName" type="text" class="settings-input" placeholder="输入新分类名称" @keyup.enter="addCategory" />
              <button class="mini-btn" @click="addCategory">确定</button>
              <button class="mini-btn ghost" @click="showCategoryInput = false; newCategoryName = ''">取消</button>
            </div>
          </div>
        </div>

        <div class="settings-row" style="margin-top: 12px;">
          <div class="settings-item">
            <label class="settings-label">封面图片</label>
            <div class="image-upload-row">
              <div class="image-upload-box" @click="triggerImageUpload">
                <img v-if="toolImagePreview" :src="toolImagePreview" />
                <div v-else class="image-upload-placeholder">
                  <span>+</span>
                  <p>点击上传封面</p>
                </div>
                <input ref="imageInput" type="file" accept="image/*" style="display:none" @change="handleImageUpload" />
              </div>
              <div class="image-upload-hint">
                <p>支持 JPG、PNG 格式</p>
                <button v-if="toolImagePreview" class="ghost-btn mini" @click="clearImage">清除图片</button>
              </div>
            </div>
          </div>
        </div>

        <div class="settings-row" style="margin-top: 12px;">
          <div class="settings-item" style="flex: 1;">
            <label class="settings-label">📖 使用指南（支持富文本）</label>
            <RichEditor v-model="globalConfig.guide" placeholder="请输入工具的使用指南，支持加粗、标题、列表等格式..." />
          </div>
        </div>
      </div>
    </div>

    <!-- 主体左右分栏 -->
    <div class="main-layout">
      <!-- 左侧：参数定义表 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">📋 参数定义</span>
          <button class="add-param-btn" @click="addParam">
            <span>+</span> 添加参数
          </button>
        </div>
        <div class="param-table-wrapper">
          <div v-for="(param, index) in params" :key="param.id" class="param-card" :class="{ 'dragging': dragIndex === index }" draggable="true" @dragstart="handleDragStart(index, $event)" @dragover.prevent @drop="handleDrop(index, $event)">
            <div class="param-card-header">
              <span class="drag-handle">⋮⋮</span>
              <span class="param-index">#{{ index + 1 }}</span>
              <span class="param-badge" :class="param.type">{{ getTypeLabel(param.type) }}</span>
              <span v-if="param.required" class="required-badge">必填</span>
              <button class="remove-btn" @click="removeParam(index)" title="删除参数">×</button>
            </div>
            <div class="param-card-body">
              <div class="form-row">
                <div class="form-col">
                  <label>参数名 <span class="hint">(-xxx)</span></label>
                  <input v-model="param.name" type="text" placeholder="例如：known" @input="param.name = param.name.replace(/^-/, '')" />
                </div>
                <div class="form-col">
                  <label>标签/解释</label>
                  <input v-model="param.label" type="text" placeholder="例如：已知基因家族文件" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-col">
                  <label>参数类型</label>
                  <select v-model="param.type" @change="onTypeChange(param)">
                    <option value="text">文本</option>
                    <option value="int">整数</option>
                    <option value="float">小数</option>
                    <option value="select">下拉选择</option>
                    <option value="color">SCI配色</option>
                    <option value="palette">配色方案</option>
                    <option value="file">文件上传</option>
                    <option value="flag">开关/Flag</option>
                  </select>
                </div>
                <div class="form-col checkbox-col">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="param.required" />
                    <span>是否必填</span>
                  </label>
                </div>
              </div>
              <!-- 高级配置：根据类型动态显示 -->
              <div class="advanced-config">
                <!-- 下拉选择选项 -->
                <div v-if="param.type === 'select'" class="config-row">
                  <label>选项列表 <span class="hint">（逗号分隔）</span></label>
                  <input v-model="param.optionsText" type="text" placeholder="例如：选项A,选项B,选项C" @input="parseOptions(param)" />
                  <div v-if="param.options && param.options.length" class="options-preview">
                    <span v-for="(opt, i) in param.options" :key="i" class="option-tag">{{ opt }}</span>
                  </div>
                </div>
                <!-- SCI配色颜色列表 -->
                <div v-if="param.type === 'color'" class="config-row">
                  <label>可选颜色 <span class="hint">（逗号分隔的HEX，如 #E64B35,#4DBBD5）</span></label>
                  <input v-model="param.optionsText" type="text" placeholder="例如：#E64B35,#4DBBD5,#00A087" @input="parseColorOptions(param)" />
                  <div v-if="param.options && param.options.length" class="color-options-preview">
                    <span v-for="(opt, i) in param.options" :key="i" class="color-option-dot" :style="{ background: opt }"></span>
                  </div>
                </div>
                <!-- 配色方案预设系列 -->
                <div v-if="param.type === 'palette'" class="config-row">
                  <label>预设配色系列</label>
                  <div v-for="(palette, pIdx) in param.palettes" :key="pIdx" class="palette-series-row">
                    <div class="palette-series-header">
                      <input v-model="palette.name" type="text" class="palette-name-input" placeholder="系列名称，如：NPG顶刊" />
                      <button class="mini-btn btn-del" @click="removePalette(param, pIdx)">删除</button>
                    </div>
                    <div class="palette-colors-edit">
                      <div v-for="(c, cIdx) in palette.colors" :key="cIdx" class="palette-color-item">
                        <input type="color" v-model="palette.colors[cIdx]" />
                        <span class="palette-hex">{{ c }}</span>
                        <button class="mini-btn ghost" @click="removePaletteColor(param, pIdx, cIdx)">×</button>
                      </div>
                      <button class="mini-btn" @click="addPaletteColor(param, pIdx)">+ 颜色</button>
                    </div>
                  </div>
                  <button class="mini-btn" style="margin-top:8px;" @click="addPalette(param)">+ 新增配色系列</button>
                </div>
                <!-- 数字类型范围 -->
                <div v-if="param.type === 'int' || param.type === 'float'" class="config-row number-config">
                  <div class="number-field">
                    <label>最小值</label>
                    <input v-model.number="param.min" type="number" placeholder="无" />
                  </div>
                  <div class="number-field">
                    <label>最大值</label>
                    <input v-model.number="param.max" type="number" placeholder="无" />
                  </div>
                  <div class="number-field">
                    <label>步长</label>
                    <input v-model.number="param.step" type="number" placeholder="1" />
                  </div>
                  <div class="number-field">
                    <label>默认值</label>
                    <input v-model="param.defaultValue" type="text" placeholder="无" />
                  </div>
                </div>
                <!-- 文本/文件/配色/配色方案默认值 -->
                <div v-if="param.type === 'text' || param.type === 'file' || param.type === 'color' || param.type === 'palette'" class="config-row">
                  <label>默认值</label>
                  <input v-model="param.defaultValue" type="text" placeholder="无默认值" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：动态表单预览 -->
      <div class="right-panel">
        <div class="panel-header">
          <span class="panel-title">👁️ 表单预览</span>
          <span class="panel-subtitle">{{ globalConfig.name || '未命名功能' }}</span>
        </div>
        <div class="form-preview-wrapper">
          <div v-if="params.length === 0" class="empty-preview">
            <div class="empty-icon">📝</div>
            <p>左侧添加参数后，此处将实时预览表单</p>
          </div>
          <div v-else class="preview-form">
            <div v-for="param in params" :key="param.id" class="preview-field">
              <label class="preview-label">
                {{ param.label || param.name || '未命名参数' }}
                <span v-if="param.required && param.type !== 'flag'" class="required-star">*</span>
                <span class="param-name-hint">(-{{ param.name || '?' }})</span>
              </label>

              <!-- 文本输入 -->
              <input v-if="param.type === 'text'" v-model="formValues[param.id]" type="text" class="preview-input" :placeholder="param.defaultValue || ''" />

              <!-- 整数输入 -->
              <input v-else-if="param.type === 'int'" v-model.number="formValues[param.id]" type="number" class="preview-input" :min="param.min" :max="param.max" :step="param.step || 1" :placeholder="param.defaultValue || ''" />

              <!-- 小数输入 -->
              <input v-else-if="param.type === 'float'" v-model.number="formValues[param.id]" type="number" class="preview-input" :min="param.min" :max="param.max" :step="param.step || 0.01" :placeholder="param.defaultValue || ''" />

              <!-- 下拉选择 -->
              <select v-else-if="param.type === 'select'" v-model="formValues[param.id]" class="preview-select">
                <option value="">请选择</option>
                <option v-for="opt in param.options" :key="opt" :value="opt">{{ opt }}</option>
              </select>

              <!-- SCI配色 -->
              <div v-else-if="param.type === 'color'" class="preview-color-picker">
                <div class="color-blocks-row">
                  <span
                    v-for="(opt, i) in (param.options && param.options.length ? param.options : defaultColorOptions)"
                    :key="i"
                    class="color-block"
                    :class="{ active: formValues[param.id] === opt }"
                    :style="{ background: opt }"
                    @click="formValues[param.id] = opt"
                    :title="opt"
                  ></span>
                </div>
                <div class="custom-color-row">
                  <input type="color" v-model="formValues[param.id]" class="native-color-picker" />
                  <span class="selected-color-hex">{{ formValues[param.id] || '未选择' }}</span>
                </div>
              </div>

              <!-- 配色方案 -->
              <div v-else-if="param.type === 'palette'" class="preview-palette-picker">
                <div class="custom-select">
                  <div class="select-trigger" @click="togglePaletteDropdown(param.id)">
                    <div class="preview-bar">
                      <span v-for="(c, i) in getPreviewColors(param, formValues[param.id]).slice(0,3)" :key="i" class="preview-block" :style="{ background: c }"></span>
                    </div>
                    <span class="trigger-text">{{ getPaletteTriggerText(param, formValues[param.id]) }}</span>
                    <span>▼</span>
                  </div>
                  <div class="select-dropdown" :class="{ show: paletteDropdownOpen === param.id }">
                    <div v-for="(palette, pIdx) in param.palettes" :key="pIdx" class="select-option" @click="selectPaletteOption(param.id, palette.colors.join(','))">
                      <div class="option-preview-bar">
                        <span v-for="(c, i) in palette.colors.slice(0,3)" :key="i" class="option-preview-block" :style="{ background: c }"></span>
                      </div>
                      <span>{{ palette.name }}</span>
                    </div>
                    <div class="select-option" @click="selectPaletteOption(param.id, 'custom')">
                      <span>🔧 自定义配色</span>
                    </div>
                  </div>
                </div>
                <div v-if="getPaletteMode(param, formValues[param.id]) === 'custom'" class="custom-palette-wrap">
                  <div class="custom-palette-colors">
                    <div v-for="(c, i) in getCustomPaletteColors(formValues[param.id])" :key="i" class="custom-palette-item">
                      <input type="color" :value="c" @input="updateCustomPaletteColor(param.id, i, $event.target.value)" />
                      <span class="palette-hex">{{ c }}</span>
                      <button class="mini-btn ghost" @click="removeCustomPaletteColor(param.id, i)">×</button>
                    </div>
                    <button class="mini-btn" @click="addCustomPaletteColor(param.id)">+ 颜色</button>
                  </div>
                </div>
                <div class="palette-value-preview">
                  <span class="palette-value-label">当前值：</span>
                  <code>{{ formValues[param.id] || '未选择' }}</code>
                </div>
              </div>

              <!-- 文件上传 -->
              <div v-else-if="param.type === 'file'" class="preview-file">
                <input type="file" @change="(e) => handleFileChange(e, param.id)" class="file-input" />
                <span v-if="fileNames[param.id]" class="file-selected-name">📎 {{ fileNames[param.id] }}</span>
              </div>

              <!-- 开关/Flag -->
              <label v-else-if="param.type === 'flag'" class="preview-checkbox">
                <input type="checkbox" v-model="formValues[param.id]" />
                <span class="check-slider"></span>
                <span class="check-label">启用 {{ param.label || param.name }}</span>
              </label>
            </div>

            <button class="submit-btn" @click="handleSubmit">🚀 提交并生成命令</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部命令行输出 -->
    <div v-if="generatedCommand" class="command-output" :class="{ 'has-error': hasError }">
      <div class="command-header">
        <span class="command-title">{{ hasError ? '⚠️ 警告' : '✅ 生成的命令' }}</span>
        <button class="copy-btn" @click="copyCommand">📋 复制</button>
      </div>
      <div class="command-body">
        <pre><code>{{ generatedCommand }}</code></pre>
      </div>
      <div v-if="hasError" class="command-error">
        {{ errorMessage }}
      </div>
      <div v-if="fileWarning" class="command-warning">
        💡 文件参数仅使用文件名，请确保文件已处于脚本可访问路径。
      </div>
    </div>

    <!-- Toast -->
    <div v-if="showToast" :class="['toast', toastType]">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { createToolApi, updateToolApi, getToolDetail, getCategories, createCategoryApi, deleteCategoryApi } from '../api';
import RichEditor from '../components/RichEditor.vue';

let idCounter = 1;

function generateId() {
  return 'param_' + Date.now() + '_' + (idCounter++);
}

const DEFAULT_PARAMS = [
  { name: 'known', label: '已知基因家族文件', type: 'file', required: true, defaultValue: '', id: generateId() },
  {
    name: 'fa', label: '基因组序列', type: 'select', required: true,
    optionsText: '/media/desk16/test02/data/plantGIR/OG/OG001,/media/desk16/test02/data/plantGIR/OG/OG002,/media/desk16/test02/data/plantGIR/OG/OG003',
    options: ['/media/desk16/test02/data/plantGIR/OG/OG001', '/media/desk16/test02/data/plantGIR/OG/OG002', '/media/desk16/test02/data/plantGIR/OG/OG003'],
    defaultValue: '', id: generateId()
  },
  { name: 'Fname', label: '基因家族名称', type: 'text', required: true, defaultValue: '', id: generateId() },
  { name: 'od', label: '输出目录', type: 'text', required: true, defaultValue: './output', id: generateId() },
  { name: 'pfam', label: 'Pfam结构域文件', type: 'file', required: false, defaultValue: '', id: generateId() },
  { name: 'ratio', label: '比例阈值', type: 'float', required: false, min: 0.1, max: 0.8, step: 0.01, defaultValue: '0.5', id: generateId() },
  { name: 'group', label: '分组信息文件', type: 'file', required: false, defaultValue: '', id: generateId() },
  { name: 'identity', label: '一致性阈值(%)', type: 'int', required: false, min: 0, max: 100, step: 1, defaultValue: '', id: generateId() },
  { name: 'score', label: '得分阈值', type: 'float', required: false, min: null, max: null, step: 0.1, defaultValue: '', id: generateId() },
  { name: 'combine', label: '合并结果', type: 'flag', required: false, defaultValue: false, id: generateId() },
  { name: 'only', label: '仅鉴定', type: 'flag', required: false, defaultValue: false, id: generateId() },
];

const DEFAULT_CONFIG = {
  name: '基因家族鉴定',
  scriptPath: 'perl /path/to/gene_family_identify.pl',
  category: '科研工具',
  guide: ''
};

export default {
  name: 'AddTool',
  components: { RichEditor },
  props: ['id'],
  data() {
    return {
      globalConfig: { ...DEFAULT_CONFIG },
      params: [],
      formValues: {},
      fileNames: {},
      generatedCommand: '',
      hasError: false,
      errorMessage: '',
      fileWarning: false,
      dragIndex: null,
      paletteDropdownOpen: null,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      saving: false,
      toolImagePreview: '',
      toolImageBase64: '',
      categories: [],
      newCategoryName: '',
      showCategoryInput: false
    }
  },
  computed: {
    isEditMode() {
      return !!this.id;
    }
  },
  created() {
    if (this.isEditMode) {
      this.loadToolForEdit();
    } else {
      this.resetToDefault();
    }
  },
  mounted() {
    document.addEventListener('click', this.handlePaletteDocClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handlePaletteDocClick);
  },
  methods: {
    goBack() {
      this.$router.push('/main');
    },
    async loadToolForEdit() {
      this.saving = true;
      try {
        const res = await getToolDetail(this.id);
        if (res.code === 200) {
          const tool = res.data;
          this.globalConfig = {
            name: tool.title || '',
            scriptPath: tool.script_path || '',
            category: tool.category || '科研工具',
            guide: tool.guide || ''
          };
          this.loadCategories();
          this.toolImagePreview = tool.image || '';
          this.toolImageBase64 = tool.image || '';
          // 转换参数格式回 AddTool 内部格式
          this.params = (tool.params || []).map(p => {
            const isPalette = p.type === 'palette';
            return {
              id: generateId(),
              name: p.name || '',
              label: p.label || '',
              type: this.reverseTypeMap(p.type),
              required: !!p.required,
              defaultValue: p.default !== undefined ? String(p.default) : (p.defaultValue || ''),
              optionsText: (!isPalette && (p.options || []).join(',')) || '',
              options: (!isPalette && p.options) || [],
              palettes: isPalette ? (p.options || []) : [],
              min: p.min !== undefined ? p.min : null,
              max: p.max !== undefined ? p.max : null,
              step: p.step !== undefined ? p.step : null
            };
          });
          this.initFormValues();
        } else {
          this.showToastMessage('加载工具信息失败', 'error');
          this.$router.push('/main');
        }
      } catch (e) {
        this.showToastMessage('加载工具信息失败', 'error');
        this.$router.push('/main');
      } finally {
        this.saving = false;
      }
    },
    reverseTypeMap(type) {
      const map = {
        'number': 'float',
        'switch': 'flag',
        'checkbox': 'flag',
        'text': 'text',
        'select': 'select',
        'color': 'color',
        'palette': 'palette',
        'file': 'file',
        'textarea': 'text'
      };
      return map[type] || type;
    },
    resetToDefault() {
      this.globalConfig = { ...DEFAULT_CONFIG };
      // 深拷贝默认参数
      this.params = DEFAULT_PARAMS.map(p => ({ ...p, id: generateId() }));
      this.initFormValues();
      this.generatedCommand = '';
      this.hasError = false;
      this.errorMessage = '';
      this.fileWarning = false;
      this.toolImagePreview = '';
      this.toolImageBase64 = '';
      this.loadCategories();
    },
    async loadCategories() {
      try {
        const res = await getCategories();
        if (res.code === 200) {
          this.categories = res.data || [];
        }
      } catch (e) {
        console.error('加载分类失败', e);
      }
    },
    async addCategory() {
      const name = this.newCategoryName.trim();
      if (!name) {
        this.showToastMessage('请输入分类名称', 'warning');
        return;
      }
      try {
        const res = await createCategoryApi({ name });
        if (res.code === 200) {
          this.categories.push(name);
          this.globalConfig.category = name;
          this.newCategoryName = '';
          this.showCategoryInput = false;
          this.showToastMessage('分类创建成功', 'success');
        } else {
          this.showToastMessage(res.message || '创建失败', 'error');
        }
      } catch (e) {
        this.showToastMessage('创建分类失败', 'error');
      }
    },
    async removeCategory(name) {
      if (!confirm(`确定要删除分类 "${name}" 吗？`)) return;
      try {
        const res = await deleteCategoryApi(name);
        if (res.code === 200) {
          this.categories = this.categories.filter(c => c !== name);
          if (this.globalConfig.category === name) {
            this.globalConfig.category = this.categories[0] || '其他';
          }
          this.showToastMessage('分类删除成功', 'success');
        } else {
          this.showToastMessage(res.message || '删除失败', 'error');
        }
      } catch (e) {
        this.showToastMessage('删除分类失败', 'error');
      }
    },
    onCategoryChange(e) {
      if (e.target.value === '__new__') {
        this.showCategoryInput = true;
        this.globalConfig.category = this.categories[0] || '其他';
      }
    },
    triggerImageUpload() {
      this.$refs.imageInput.click();
    },
    handleImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (res) => {
        this.toolImagePreview = res.target.result;
        this.toolImageBase64 = res.target.result;
      };
      reader.readAsDataURL(file);
    },
    clearImage() {
      this.toolImagePreview = '';
      this.toolImageBase64 = '';
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
    },
    initFormValues() {
      this.formValues = {};
      this.fileNames = {};
      this.params.forEach(param => {
        if (param.type === 'flag') {
          this.$set(this.formValues, param.id, false);
        } else if (param.type === 'select') {
          this.$set(this.formValues, param.id, param.defaultValue || '');
        } else {
          this.$set(this.formValues, param.id, param.defaultValue || '');
        }
      });
    },
    addParam() {
      const newParam = {
        id: generateId(),
        name: '',
        label: '',
        type: 'text',
        required: false,
        defaultValue: '',
        optionsText: '',
        options: [],
        min: null,
        max: null,
        step: null
      };
      this.params.push(newParam);
      this.$set(this.formValues, newParam.id, '');
    },
    removeParam(index) {
      const param = this.params[index];
      this.$delete(this.formValues, param.id);
      this.$delete(this.fileNames, param.id);
      this.params.splice(index, 1);
    },
    onTypeChange(param) {
      // 切换类型时重置相关配置
      if (param.type === 'flag') {
        this.$set(this.formValues, param.id, false);
      } else if (param.type === 'select') {
        this.parseOptions(param);
        this.$set(this.formValues, param.id, '');
      } else if (param.type === 'color') {
        this.parseColorOptions(param);
        this.$set(this.formValues, param.id, '');
      } else if (param.type === 'palette') {
        if (!param.palettes) {
          this.$set(param, 'palettes', []);
        }
        this.$set(this.formValues, param.id, '');
      } else {
        this.$set(this.formValues, param.id, param.defaultValue || '');
      }
    },
    parseOptions(param) {
      if (param.optionsText) {
        param.options = param.optionsText.split(',').map(s => s.trim()).filter(s => s);
      } else {
        param.options = [];
      }
    },
    parseColorOptions(param) {
      if (param.optionsText) {
        param.options = param.optionsText.split(',').map(s => s.trim()).filter(s => s);
      } else {
        param.options = [];
      }
    },
    getTypeLabel(type) {
      const map = {
        text: '文本',
        int: '整数',
        float: '小数',
        select: '下拉',
        color: 'SCI配色',
        palette: '配色方案',
        file: '文件',
        flag: '开关'
      };
      return map[type] || type;
    },
    defaultColorOptions() {
      return ['#E64B35', '#4DBBD5', '#00A087', '#3C5488', '#F39B7F', '#8491B4', '#91D1C2', '#DC0000', '#7E6148', '#B09C85'];
    },
    handleFileChange(e, paramId) {
      const file = e.target.files[0];
      if (file) {
        this.$set(this.fileNames, paramId, file.name);
        this.$set(this.formValues, paramId, file.name);
      }
    },
    handlePaletteDocClick(e) {
      const selectors = document.querySelectorAll('.preview-palette-picker .custom-select');
      let inside = false;
      selectors.forEach(el => {
        if (el.contains(e.target)) inside = true;
      });
      if (!inside) {
        this.paletteDropdownOpen = null;
      }
    },
    handleDragStart(index, e) {
      this.dragIndex = index;
      e.dataTransfer.effectAllowed = 'move';
    },
    handleDrop(index, e) {
      e.preventDefault();
      if (this.dragIndex === null || this.dragIndex === index) return;
      const moved = this.params[this.dragIndex];
      this.params.splice(this.dragIndex, 1);
      this.params.splice(index, 0, moved);
      this.dragIndex = null;
    },
    // ========== 配色方案管理方法 ==========
    addPalette(param) {
      if (!param.palettes) {
        this.$set(param, 'palettes', []);
      }
      param.palettes.push({ name: '新系列', colors: ['#E64B35', '#4DBBD5', '#00A087'] });
    },
    removePalette(param, idx) {
      param.palettes.splice(idx, 1);
    },
    addPaletteColor(param, pIdx) {
      param.palettes[pIdx].colors.push('#cccccc');
    },
    removePaletteColor(param, pIdx, cIdx) {
      param.palettes[pIdx].colors.splice(cIdx, 1);
    },
    // ========== 预览区配色方案方法 ==========
    togglePaletteDropdown(paramId) {
      this.paletteDropdownOpen = this.paletteDropdownOpen === paramId ? null : paramId;
    },
    selectPaletteOption(paramId, value) {
      this.paletteDropdownOpen = null;
      this.$set(this.formValues, paramId, value === 'custom' ? '#ff0000,#00ff00,#0000ff' : value);
    },
    getPreviewColors(param, value) {
      if (!value) return [];
      if (value === 'custom') return ['#ff0000', '#00ff00', '#0000ff'];
      return value.split(',').filter(c => c);
    },
    getPaletteTriggerText(param, value) {
      if (!value) return '请选择配色';
      if (value === 'custom') return '自定义配色';
      // 查找是否是某个预设系列
      const colors = value.split(',').filter(c => c);
      if (param.palettes) {
        for (const pal of param.palettes) {
          if (pal.colors.join(',') === value) {
            return pal.name;
          }
        }
      }
      return '自定义配色';
    },
    getPaletteMode(param, value) {
      if (!value) return 'none';
      // 检查是否匹配某个预设系列
      if (param.palettes) {
        for (const pal of param.palettes) {
          if (pal.colors.join(',') === value) {
            return 'preset';
          }
        }
      }
      return 'custom';
    },
    getCustomPaletteColors(value) {
      if (!value) return [];
      return value.split(',').filter(c => c);
    },
    updateCustomPaletteColor(paramId, idx, color) {
      const colors = this.formValues[paramId].split(',').filter(c => c);
      colors[idx] = color;
      this.$set(this.formValues, paramId, colors.join(','));
    },
    addCustomPaletteColor(paramId) {
      const colors = this.formValues[paramId] ? this.formValues[paramId].split(',').filter(c => c) : [];
      colors.push('#cccccc');
      this.$set(this.formValues, paramId, colors.join(','));
    },
    removeCustomPaletteColor(paramId, idx) {
      const colors = this.formValues[paramId].split(',').filter(c => c);
      colors.splice(idx, 1);
      this.$set(this.formValues, paramId, colors.join(','));
    },
    handleSubmit() {
      this.hasError = false;
      this.errorMessage = '';
      this.fileWarning = false;

      // 验证必填参数
      const missingParams = [];
      for (const param of this.params) {
        if (param.required && param.type !== 'flag') {
          const val = this.formValues[param.id];
          if (val === '' || val === null || val === undefined) {
            missingParams.push(param.label || param.name);
          }
        }
      }

      if (missingParams.length > 0) {
        this.hasError = true;
        this.errorMessage = `以下必填参数缺失：${missingParams.join('、')}`;
        this.generatedCommand = '';
        this.showToastMessage(this.errorMessage, 'error');
        return;
      }

      // 拼装命令行
      const parts = [];
      if (this.globalConfig.scriptPath.trim()) {
        parts.push(this.globalConfig.scriptPath.trim());
      }

      let hasFileParam = false;
      for (const param of this.params) {
        const val = this.formValues[param.id];
        if (param.type === 'flag') {
          if (val) {
            parts.push(`-${param.name}`);
          }
        } else if (val !== '' && val !== null && val !== undefined) {
          parts.push(`-${param.name}`);
          parts.push(String(val));
          if (param.type === 'file') {
            hasFileParam = true;
          }
        }
      }

      this.generatedCommand = parts.join(' ');
      this.fileWarning = hasFileParam;
      this.showToastMessage('命令生成成功！', 'success');
    },
    copyCommand() {
      if (!this.generatedCommand) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.generatedCommand).then(() => {
          this.showToastMessage('已复制到剪贴板', 'success');
        });
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = this.generatedCommand;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.showToastMessage('已复制到剪贴板', 'success');
      }
    },
    async saveTool() {
      if (!this.globalConfig.name.trim()) {
        this.showToastMessage('请填写功能名称', 'error');
        return;
      }
      if (!this.globalConfig.scriptPath.trim()) {
        this.showToastMessage('请填写脚本全路径', 'error');
        return;
      }
      if (this.params.length === 0) {
        this.showToastMessage('请至少添加一个参数', 'error');
        return;
      }
      // 验证参数名是否都填写了
      const emptyNames = this.params.filter(p => !p.name.trim());
      if (emptyNames.length > 0) {
        this.showToastMessage('存在未填写参数名的参数', 'error');
        return;
      }

      this.saving = true;
      try {
        // 转换参数格式以兼容 ToolDetail
        const convertedParams = this.params.map(p => {
          const cp = {
            name: p.name,
            label: p.label || p.name,
            required: !!p.required,
            description: ''
          };
          if (p.type === 'flag') {
            cp.type = 'switch';
            cp.default = false;
          } else if (p.type === 'int' || p.type === 'float') {
            cp.type = 'number';
            cp.min = p.min;
            cp.max = p.max;
            cp.step = p.step || (p.type === 'int' ? 1 : 0.01);
            cp.default = p.defaultValue !== undefined && p.defaultValue !== '' ? parseFloat(p.defaultValue) : undefined;
          } else if (p.type === 'select') {
            cp.type = 'select';
            cp.options = p.options || [];
            cp.default = p.defaultValue || '';
          } else if (p.type === 'color') {
            cp.type = 'color';
            cp.options = p.options || [];
            cp.default = p.defaultValue || '';
          } else if (p.type === 'palette') {
            cp.type = 'palette';
            cp.options = p.palettes || [];
            cp.default = p.defaultValue || '';
          } else if (p.type === 'file') {
            cp.type = 'file';
            cp.default = p.defaultValue || '';
          } else {
            cp.type = 'text';
            cp.default = p.defaultValue || '';
            cp.placeholder = p.defaultValue || '';
          }
          return cp;
        });

        const payload = {
          title: this.globalConfig.name.trim(),
          description: '',
          script_path: this.globalConfig.scriptPath.trim(),
          params: convertedParams,
          category: this.globalConfig.category || '科研工具',
          tags: [this.globalConfig.category || '动态工具'],
          image: this.toolImageBase64 || '/static/imgs/default_tool.jpg',
          guide: this.globalConfig.guide || ''
        };

        let res;
        if (this.isEditMode) {
          res = await updateToolApi(this.id, payload);
        } else {
          res = await createToolApi(payload);
        }
        if (res.code === 200) {
          this.showToastMessage(this.isEditMode ? '工具更新成功！' : '工具保存成功！即将跳转到工具列表', 'success');
          setTimeout(() => {
            this.$router.push('/main');
          }, 1500);
        } else {
          this.showToastMessage(res.message || '保存失败', 'error');
        }
      } catch (error) {
        console.error('Save tool error:', error);
        const msg = error.response?.data?.message || error.message || '保存失败';
        this.showToastMessage(msg, 'error');
      } finally {
        this.saving = false;
      }
    },
    showToastMessage(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    }
  },
  watch: {
    params: {
      deep: true,
      handler(newParams) {
        // 同步新增参数到 formValues
        newParams.forEach(param => {
          if (!(param.id in this.formValues)) {
            if (param.type === 'flag') {
              this.$set(this.formValues, param.id, false);
            } else {
              this.$set(this.formValues, param.id, param.defaultValue || '');
            }
          }
        });
      }
    }
  }
}
</script>

<style scoped>
.add-tool-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f4f8;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* 顶部导航栏 */
.top-bar {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  text-align: center;
}

.actions {
  display: flex;
  gap: 10px;
}

.ghost-btn {
  background: #fff;
  border: 1px solid #cbd5e1;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  color: #475569;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.ghost-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.icon {
  font-size: 16px;
}

.primary-btn {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(14, 165, 233, 0.25);
}

.primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(14, 165, 233, 0.35);
}

.primary-btn:active {
  transform: translateY(0);
}

.save-tool-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25);
  display: flex;
  align-items: center;
  gap: 6px;
}

.save-tool-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.35);
}

.save-tool-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 图片上传 */
.image-upload-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.image-upload-box {
  width: 120px;
  height: 120px;
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s;
  background: #f8fafc;
  flex-shrink: 0;
}

.image-upload-box:hover {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.image-upload-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #94a3b8;
}

.image-upload-placeholder span {
  font-size: 32px;
  line-height: 1;
}

.image-upload-placeholder p {
  font-size: 12px;
  margin-top: 4px;
}

.image-upload-hint {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.image-upload-hint p {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

.ghost-btn.mini {
  padding: 4px 10px;
  font-size: 12px;
  width: fit-content;
}

/* 全局设置 */
.global-settings {
  padding: 16px 24px 0;
  flex-shrink: 0;
  max-height: 38vh;
  overflow-y: auto;
}

.settings-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 12px;
}

.settings-row {
  display: flex;
  gap: 16px;
}

.settings-item {
  flex: 1;
}

.settings-item.flex-2 {
  flex: 2;
}

.settings-label {
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
  font-weight: 500;
}

.settings-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.2s;
  background: #f8fafc;
}

.settings-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12);
  background: #fff;
}

/* 主体布局 */
.main-layout {
  flex: 1;
  display: flex;
  overflow: hidden;
  padding: 16px 24px;
  gap: 16px;
  min-height: 0;
}

.left-panel,
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  min-width: 0;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
  flex-shrink: 0;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #334155;
}

.panel-subtitle {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 400;
}

.add-param-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.2);
}

.add-param-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3);
}

/* 参数表格区域 */
.param-table-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
}

.param-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  margin-bottom: 12px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.param-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border-color: #cbd5e1;
}

.param-card.dragging {
  opacity: 0.6;
  border-color: #0ea5e9;
}

.param-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid #f1f5f9;
}

.drag-handle {
  color: #cbd5e1;
  cursor: grab;
  font-size: 12px;
  user-select: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.param-index {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
  min-width: 28px;
}

.param-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 500;
}

.param-badge.text { background: #e0f2fe; color: #0369a1; }
.param-badge.int { background: #dcfce7; color: #166534; }
.param-badge.float { background: #fef3c7; color: #92400e; }
.param-badge.select { background: #f3e8ff; color: #7e22ce; }
.param-badge.file { background: #ffe4e6; color: #be123c; }
.param-badge.flag { background: #ecfdf5; color: #047857; }
.param-badge.palette { background: #fdf4ff; color: #a21caf; }

.required-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  background: #fee2e2;
  color: #b91c1c;
  font-weight: 500;
}

.param-card-header .remove-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.2s;
}

.param-card-header .remove-btn:hover {
  color: #ef4444;
}

.param-card-body {
  padding: 14px;
}

.form-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.form-col {
  flex: 1;
}

.form-col label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-col input,
.form-col select {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #334155;
  background: #fff;
  transition: all 0.2s;
}

.form-col input:focus,
.form-col select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.checkbox-col {
  display: flex;
  align-items: flex-end;
  padding-bottom: 4px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px !important;
  color: #475569 !important;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #0ea5e9;
}

.hint {
  color: #94a3b8;
  font-weight: 400;
}

/* 高级配置 */
.advanced-config {
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}

.config-row label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 5px;
  font-weight: 500;
}

.config-row input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  color: #334155;
  background: #fff;
  transition: all 0.2s;
}

.config-row input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.number-config {
  display: flex;
  gap: 10px;
}

.number-field {
  flex: 1;
}

.number-field label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
}

.number-field input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
}

.options-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.option-tag {
  background: #e0f2fe;
  color: #0369a1;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 20px;
}

.color-options-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.color-option-dot {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-color-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.color-blocks-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.color-block {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  transition: all 0.2s;
}

.color-block:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.color-block.active {
  border-color: #1e293b;
  transform: scale(1.1);
  position: relative;
}

.color-block.active::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  line-height: 1;
}

.custom-color-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.native-color-picker {
  width: 40px;
  height: 32px;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
}

.hex-input {
  width: 90px;
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  color: #1e293b;
  text-align: center;
}

.hex-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12);
}

/* 右侧表单预览 */
.form-preview-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #94a3b8;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.preview-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preview-label {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-star {
  color: #ef4444;
  font-weight: bold;
}

.param-name-hint {
  color: #94a3b8;
  font-weight: 400;
  font-size: 12px;
}

.preview-input,
.preview-select {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s;
}

.preview-input:focus,
.preview-select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  background: #fff;
}

.preview-file {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.file-input {
  padding: 8px 0;
  font-size: 14px;
}

.file-selected-name {
  font-size: 13px;
  color: #0284c7;
  background: #e0f2fe;
  padding: 6px 12px;
  border-radius: 6px;
  display: inline-block;
}

.preview-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 0;
}

.preview-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #0ea5e9;
  cursor: pointer;
}

.check-label {
  font-size: 14px;
  color: #475569;
}

.submit-btn {
  margin-top: 8px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.35);
}

.submit-btn:active {
  transform: translateY(0);
}

/* 底部命令行输出 */
.command-output {
  margin: 0 24px 16px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
  overflow: hidden;
}

.command-output.has-error {
  border-color: #fca5a5;
  background: #fef2f2;
}

.command-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
}

.command-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.command-output.has-error .command-title {
  color: #b91c1c;
}

.copy-btn {
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #475569;
  transition: all 0.2s;
}

.copy-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.command-body {
  padding: 14px 18px;
}

.command-body pre {
  margin: 0;
  background: #1e293b;
  color: #e2e8f0;
  padding: 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.command-body code {
  font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
}

.command-error {
  padding: 10px 18px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 500;
  border-top: 1px solid #fecaca;
}

.command-warning {
  padding: 10px 18px;
  background: #fef3c7;
  color: #92400e;
  font-size: 13px;
  border-top: 1px solid #fde68a;
}

/* Toast */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  animation: slideDown 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.toast.success { background: linear-gradient(135deg, #10b981, #059669); }
.toast.error { background: linear-gradient(135deg, #ef4444, #dc2626); }
.toast.warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
.toast.info { background: linear-gradient(135deg, #3b82f6, #2563eb); }

@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* 滚动条美化 */
.param-table-wrapper::-webkit-scrollbar,
.form-preview-wrapper::-webkit-scrollbar,
.command-body pre::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.param-table-wrapper::-webkit-scrollbar-track,
.form-preview-wrapper::-webkit-scrollbar-track,
.command-body pre::-webkit-scrollbar-track {
  background: transparent;
}

.param-table-wrapper::-webkit-scrollbar-thumb,
.form-preview-wrapper::-webkit-scrollbar-thumb,
.command-body pre::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.param-table-wrapper::-webkit-scrollbar-thumb:hover,
.form-preview-wrapper::-webkit-scrollbar-thumb:hover,
.command-body pre::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.category-select-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-select-row select {
  flex: 1;
}

.remove-category-btn {
  background: #fee2e2;
  color: #b91c1c;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.remove-category-btn:hover {
  background: #fecaca;
}

.new-category-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.new-category-row input {
  flex: 1;
}

/* 配色方案配置 */
.palette-series-row {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}
.palette-series-header {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.palette-name-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
}
.palette-colors-edit {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.palette-color-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8fafc;
  padding: 4px 8px;
  border-radius: 6px;
}
.palette-color-item input[type="color"] {
  width: 28px;
  height: 28px;
  border: none;
  padding: 0;
  cursor: pointer;
}
.palette-hex {
  font-size: 11px;
  color: #64748b;
  font-family: monospace;
}

/* 预览区配色方案 */
.preview-palette-picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.preview-palette-picker .custom-select {
  position: relative;
  width: 100%;
}
.preview-palette-picker .select-trigger {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.preview-palette-picker .preview-bar {
  display: flex;
  gap: 3px;
}
.preview-palette-picker .preview-block {
  width: 14px;
  height: 14px;
  border-radius: 2px;
}
.preview-palette-picker .trigger-text {
  flex: 1;
  font-size: 13px;
  color: #334155;
}
.preview-palette-picker .select-dropdown {
  position: absolute;
  top: 110%;
  left: 0;
  right: 0;
  border: 1px solid #eee;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  display: none;
  z-index: 999;
  max-height: 300px;
  overflow-y: auto;
}
.preview-palette-picker .select-dropdown.show {
  display: block;
}
.preview-palette-picker .select-option {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.preview-palette-picker .select-option:hover {
  background: #f5f7fa;
}
.preview-palette-picker .option-preview-bar {
  display: flex;
  gap: 3px;
}
.preview-palette-picker .option-preview-block {
  width: 14px;
  height: 14px;
  border-radius: 2px;
}
.custom-palette-wrap {
  margin-top: 4px;
  padding: 10px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}
.custom-palette-colors {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.custom-palette-item {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}
.custom-palette-item input[type="color"] {
  width: 28px;
  height: 28px;
  border: none;
  padding: 0;
  cursor: pointer;
}
.palette-value-preview {
  font-size: 12px;
  color: #64748b;
}
.palette-value-preview code {
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
}

.mini-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  transition: all 0.2s;
}

.mini-btn.ghost {
  background: #f1f5f9;
  color: #64748b;
}

.mini-btn:hover {
  transform: translateY(-1px);
}

/* 响应式 */
@media screen and (max-width: 1024px) {
  .main-layout {
    flex-direction: column;
  }
  .left-panel,
  .right-panel {
    max-height: 50vh;
  }
}
</style>
